import { useState, type ReactNode } from "react";
import {
  Alert,
  ApiError,
  Button,
  Modal,
  apiFetch,
  caregiverState,
  cooldownMessage,
  useNow,
  type AlertTone,
  type PortalCaregiver,
} from "@dearbill/shared";
import { HomeCard } from "../home/HomeCard";
import { AddCaregiverModal } from "./AddCaregiverModal";
import { CaregiverRow } from "./CaregiverRow";

const API_BASE = import.meta.env.VITE_API_BASE_URL ?? "";
/** The frame's 48px action-button geometry, shared by every control in the tile. */
const TILE_BUTTON = "h-12 self-start py-0";
const GENERIC_ERROR = "Something went wrong. Please try again.";
/** The countdown tick; the pending row flips to expired within this of the real expiry. */
const TICK_MS = 30_000;

interface Notice {
  tone: AlertTone;
  title: string;
}

interface CaregiverCardProps {
  caregiver: PortalCaregiver | null;
  /** A caregiver session: the tile shows the caregiver but no Add, Delete or Resend control. */
  readOnly: boolean;
  /** Portal session bearer for the add, delete and resend. */
  session: string;
  /** Refetches the dashboard; the fresh payload drives the tile after every action. */
  reload: () => void;
  /** Called when a request is rejected with 401. */
  onSignOut: () => void;
}

/**
 * The Authorized Caregivers tile: the safety notice, then the state the
 * dashboard's `caregiver` block puts it in. Empty offers "Add caregiver",
 * which opens the invitation form; a caregiver shows as a `CaregiverRow` with
 * "Delete" (a confirm dialog, then `DELETE /api/portal/caregiver`) and, once
 * the invitation has expired, "Resend invitation" (`POST
 * /api/portal/caregiver/renotify`). Every action ends in `reload`, except a
 * 429 cooldown, whose wait shows in place under the row; a 404 on delete and
 * a 409 on resend mean the state moved on, and the refetch shows how. Other
 * failures show one generic error and refetch too.
 */
export function CaregiverCard({
  caregiver,
  readOnly,
  session,
  reload,
  onSignOut,
}: CaregiverCardProps): ReactNode {
  const now = useNow(TICK_MS);
  const [adding, setAdding] = useState(false);
  // A new key per opening gives the form a fresh, blank instance each time.
  const [addKey, setAddKey] = useState(0);
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<Notice | null>(null);
  const state = caregiver === null ? null : caregiverState(caregiver, now);

  function openAdd(): void {
    setAddKey((n) => n + 1);
    setAdding(true);
  }

  async function remove(): Promise<void> {
    setBusy(true);
    setNotice(null);
    try {
      await apiFetch(
        "/api/portal/caregiver",
        { method: "DELETE" },
        { baseUrl: API_BASE, token: session },
      );
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        onSignOut();
        return;
      }
      if (!(err instanceof ApiError && err.status === 404)) {
        setNotice({ tone: "error", title: GENERIC_ERROR });
      }
    } finally {
      setBusy(false);
      setConfirming(false);
    }
    reload();
  }

  async function resend(): Promise<void> {
    setBusy(true);
    setNotice(null);
    try {
      await apiFetch<PortalCaregiver>(
        "/api/portal/caregiver/renotify",
        { method: "POST" },
        { baseUrl: API_BASE, token: session },
      );
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        onSignOut();
        return;
      }
      const cooldown = cooldownMessage(err);
      if (cooldown !== null) {
        setNotice({ tone: "warning", title: cooldown });
        return;
      }
      if (!(err instanceof ApiError && err.status === 409)) {
        setNotice({ tone: "error", title: GENERIC_ERROR });
      }
    } finally {
      setBusy(false);
    }
    reload();
  }

  return (
    <HomeCard title="Authorized Caregivers" titleSize="lg">
      <p className="text-body-sm text-slate-teal">
        <strong className="text-midnight font-bold">Important safety notice:</strong> Caregiver
        access grants full read and write access to your dashboard, including medication entries and
        support program management. It is not view-only.
      </p>
      {caregiver === null ? (
        !readOnly && (
          <Button variant="primary" className={TILE_BUTTON} onClick={openAdd}>
            Add caregiver
          </Button>
        )
      ) : (
        <>
          <CaregiverRow caregiver={caregiver} now={now} />
          {!readOnly && (
            <div className="flex flex-wrap gap-3">
              {state === "expired" && (
                <Button
                  variant="primary"
                  className={TILE_BUTTON}
                  disabled={busy}
                  onClick={() => void resend()}
                >
                  Resend invitation
                </Button>
              )}
              <Button
                variant="outlined"
                className={TILE_BUTTON}
                disabled={busy}
                onClick={() => setConfirming(true)}
              >
                Delete
              </Button>
            </div>
          )}
        </>
      )}
      {notice !== null && (
        <Alert tone={notice.tone} title={notice.title} onDismiss={() => setNotice(null)} />
      )}
      <AddCaregiverModal
        key={addKey}
        open={adding}
        session={session}
        reload={reload}
        onSignOut={onSignOut}
        onClose={() => setAdding(false)}
      />
      {caregiver !== null && (
        <Modal
          open={confirming}
          title={`Remove ${caregiver.firstName} ${caregiver.lastName} as your caregiver?`}
          onClose={() => {
            if (!busy) setConfirming(false);
          }}
        >
          <p className="text-body-sm text-slate-teal">They lose access right away.</p>
          <div className="mt-6 flex justify-end gap-3">
            <Button variant="ghost" disabled={busy} onClick={() => setConfirming(false)}>
              Cancel
            </Button>
            <Button variant="primary" disabled={busy} onClick={() => void remove()}>
              Remove
            </Button>
          </div>
        </Modal>
      )}
    </HomeCard>
  );
}
